import { ChainedLogos } from '@client/components/chained-logos';
import { Organization } from '@schema/schemas';
import { useTranslation } from 'react-i18next';
import { ty } from '@client/styles/typography';
import { Tooltip, TooltipContent } from '@arcadiapower/design-system';
import {
  CapturedDataIcon,
  CapturedDataTrigger,
  Divider,
  DividerLine,
  IntroBox,
  TooltipBody,
} from './provider-header.style';

// Shrike InfoCircleInverted path. evenodd punches the "i" out of the disc.
const INFO_CIRCLE_INVERTED =
  'M8 1.5C4.13401 1.5 1 4.63401 1 8.5C1 12.366 4.13401 15.5 8 15.5C11.866 15.5 15 12.366 15 8.5C15 4.63401 11.866 1.5 8 1.5ZM8.34924 6.30023C8.14151 6.30006 7.9385 6.23829 7.76588 6.12272C7.59327 6.00715 7.45881 5.84299 7.37951 5.65099C7.30021 5.45899 7.27963 5.24779 7.32038 5.0441C7.36114 4.8404 7.46139 4.65338 7.60846 4.50667C7.75552 4.35996 7.94279 4.26017 8.14659 4.21991C8.35038 4.17965 8.56153 4.20074 8.75333 4.28051C8.94514 4.36028 9.10897 4.49514 9.22412 4.66803C9.33926 4.84093 9.40054 5.04409 9.40021 5.25182C9.40032 5.38975 9.37319 5.52634 9.32039 5.65376C9.26758 5.78117 9.19013 5.89691 9.09248 5.99432C8.99483 6.09173 8.87891 6.16889 8.75137 6.22139C8.62382 6.27389 8.48716 6.30068 8.34924 6.30023ZM7.63116 12.7286H7.07581C7.06833 12.7214 7.06003 12.7151 7.05108 12.7098C6.69962 12.5494 6.53071 12.227 6.62455 11.8508C6.79231 11.1592 6.96387 10.4684 7.13543 9.77767L7.13556 9.77717C7.22129 9.43193 7.30703 9.0867 7.3923 8.74138C7.42751 8.59797 7.45287 8.45233 7.46822 8.30546C7.47323 8.28724 7.47305 8.26798 7.4677 8.24985C7.46236 8.23172 7.45205 8.21544 7.43796 8.20285C7.42386 8.19026 7.40653 8.18185 7.38791 8.17858C7.36929 8.17531 7.35013 8.1773 7.33259 8.18433C7.27372 8.19762 7.21651 8.21736 7.16197 8.24319C7.10992 8.265 7.05845 8.2884 7.00702 8.31178C6.93371 8.34511 6.86048 8.3784 6.78577 8.40698C6.74002 8.43555 6.68517 8.44579 6.63219 8.43567C6.57921 8.42555 6.532 8.3958 6.5 8.35238V8.15021C6.58597 8.0299 6.68339 7.9182 6.79089 7.81666C7.17136 7.51724 7.55865 7.23146 7.95361 6.94569C8.03794 6.89024 8.1284 6.84472 8.22318 6.81005C8.34692 6.75623 8.48426 6.74193 8.61644 6.7691C9.11036 6.86891 9.36628 7.26473 9.246 7.75183C9.00202 8.75048 8.75691 9.74912 8.51066 10.7478C8.47195 10.9105 8.44346 11.0754 8.42535 11.2417C8.42046 11.26 8.42106 11.2793 8.42708 11.2972C8.43309 11.3152 8.44425 11.3309 8.45917 11.3426C8.47408 11.3542 8.4921 11.3612 8.51096 11.3627C8.52983 11.3642 8.54871 11.3601 8.56526 11.3509C8.62723 11.3321 8.68694 11.3058 8.74664 11.2794L8.74667 11.2794C8.77653 11.2662 8.80639 11.2531 8.83653 11.2408C8.94572 11.1931 9.05918 11.1496 9.16922 11.1078C9.20131 11.096 9.23634 11.0947 9.26918 11.1042C9.30202 11.1138 9.33096 11.1335 9.35178 11.1607C9.37468 11.1875 9.38871 11.2208 9.39192 11.2559C9.39512 11.291 9.38736 11.3263 9.36969 11.3569C9.342 11.415 9.30672 11.4692 9.26476 11.5181C9.17315 11.6204 9.07421 11.7159 8.96875 11.8039C8.54734 12.1434 8.11569 12.4769 7.63116 12.7286Z';

export type Props = {
  organization: Organization;
};

export const ProviderHeader = ({ organization }: Props): JSX.Element => {
  const { t } = useTranslation('connect', { keyPrefix: 'components.providerForm' });

  return (
    <div style={{ paddingTop: '32px', marginBottom: '-48px' }}>
      <IntroBox>
        <ChainedLogos organization={organization} />
        <p
          className={ty('paragraph400')}
          style={{ marginTop: '32px', marginBottom: '24px', textAlign: 'center' }}
        >
          {t('headerText', { organizationName: organization.displayName || organization.name })}
        </p>
        <Tooltip>
          <CapturedDataTrigger
            aria-label={t('capturedDataTriggerLabel')}
            className={ty('capturedDataLabel')}
          >
            <CapturedDataIcon>
              <path d={INFO_CIRCLE_INVERTED} fill="currentColor" fillRule="evenodd" />
            </CapturedDataIcon>
            {t('tooltipText')}
          </CapturedDataTrigger>
          <TooltipContent side="bottom" sideOffset={4} className="max-w-sm flex-col items-start">
            <TooltipBody>
              <p className={ty('default', 'inverse')}>{t('tooltipContent1')}</p>
              <p className={ty('default', 'inverse')}>{t('tooltipContent2')}</p>
            </TooltipBody>
          </TooltipContent>
        </Tooltip>
      </IntroBox>
      <Divider>
        <DividerLine />
      </Divider>
    </div>
  );
};
